'use client';

import { useMemo, useRef, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import useSWR from 'swr';
import { toast } from '@/utils/toast';

import { addEditAddressValidation } from '@/utils/validation/user';
import { getAllCities } from '@/services/user/addresses';

import Input from '@/components/ui/inputFormik';
import SelectInputFormik from '@/components/ui/inputFormik/select-input-formik';
import { Button } from '@/components/ui/buttons';
import { Loading } from '@/components/ui/forms';

function AddEditAddressManual({
  handleOnClose,
  t,
  handleSubmit,
  mage,
  selectedAddress,
}) {
  const regionRef = useRef(null);
  const [areas, setAreas] = useState([]);

  const initialValues = {
    region_id: selectedAddress?.region_id || '',
    city_id:
      selectedAddress?.custom_attributes?.find(
        (e) => e?.attribute_code === 'city_id'
      )?.value ||
      selectedAddress?.city ||
      '',
    city: selectedAddress?.city || '',
    region: selectedAddress?.selectedAddress?.region || '',
    street: selectedAddress?.street?.[0] || '',
    buildingNumber:
      selectedAddress?.custom_attributes?.find(
        (e) => e?.attribute_code === 'building_number'
      )?.value || '',
    deliveryInstruction:
      selectedAddress?.custom_attributes?.find(
        (e) => e?.attribute_code === 'delivery_instructions'
      )?.value || '',
  };

  /** Fetch Cities */
  const {
    data: citiesData,
    error: citiesError,
    isLoading: isCitiesLoading,
  } = useSWR('/cities', () => getAllCities(mage), {
    revalidateOnFocus: false,
    onError: (error) => toast.error(error.message),
  });

  /** Memoized options */
  const cityOptions = useMemo(() => {
    return (
      citiesData?.data?.data
        // ?.filter((city) => !!city?.areas?.length)
        ?.map((city) => ({
          country_id: city.country_id,
          label: city.label,
          title: city.title,
          value: city.value,
          areas: city.areas || [],
        }))
        .slice(1) || []
    );
  }, [citiesData]);

  const getSelectedRegion = (regionId) =>
    cityOptions.find((region) => +region.value === +regionId);

  const getAreaOptions = (regionId) =>
    getSelectedRegion(regionId)?.areas?.map((area) => ({
      label: area?.name,
      value: area?.city_id,
    })) || [];

  useEffect(() => {
    if (selectedAddress?.region_id) {
      setAreas(getSelectedRegion(initialValues?.region_id)?.areas || []);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress?.region_id]);

  if (isCitiesLoading) {
    return (
      <div className='flex h-40 items-center justify-center'>
        <Loading />
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={addEditAddressValidation(t)}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, setFieldValue, values, isSubmitting }) => (
        <Form className='mt-3 mb-6 grid grid-cols-1 gap-5 px-7 max-sm:px-1.75'>
          {/* Region */}

          <SelectInputFormik
            options={cityOptions}
            placeholder={t('user.select_region_key')}
            title={t('user.region_key')}
            labelClassName='font-normal'
            name='region_id'
            id='region_id'
            isDisabled={citiesError}
            errors={errors}
            touched={touched}
            value={getSelectedRegion(values?.region_id) || ''}
            handleChange={(e) => {
              const selected = getSelectedRegion(e.value);
              setAreas(selected?.areas);

              setFieldValue('region_id', e.value);
              setFieldValue('region', e.label);
              setFieldValue('city', '');
              setFieldValue('city_id', '');

              regionRef.current?.clearValue();
            }}
          />

          {/* City */}
          <SelectInputFormik
            ref={regionRef}
            name='city_id'
            id='city_id'
            title={t('user.district_key')}
            placeholder={t('user.select_district_key')}
            errors={errors}
            touched={touched}
            options={areas?.map((area) => ({
              label: area?.name,
              value: area?.city_id,
            }))}
            value={
              getAreaOptions(values?.region_id)?.find(
                (area) => +area?.value === +values?.city_id
              ) || ''
            }
            handleChange={(e) => {
              setFieldValue('city', e?.label);
              setFieldValue('city_id', e?.value);
            }}
          />

          {/* Inputs */}
          <Input name='street' title={t('checkout.Street')} />
          <Input name='buildingNumber' title={t('modals.buildingNumber')} />
          <Input
            name='deliveryInstruction'
            as='textarea'
            title={t('modals.Additional Description')}
          />

          {/* Buttons */}
          <div className='flex items-center justify-between gap-2'>
            <Button
              type='button'
              onClick={handleOnClose}
              className='flex h-[45px] w-38 items-center justify-center rounded-[5px] border border-[#BBB] bg-linear-to-b from-[#F7F8FA] to-[#E7E9EC] text-[15px] font-bold text-black! shadow-[inset_0_1px_0_1px_#FFFFFF4D]'
            >
              {t('modals.Cancel')}
            </Button>
            <Button
              type='submit'
              disabled={isSubmitting}
              className='bg-primary h-[45px] w-[16rem] rounded-[5px] text-[15px] font-bold text-white'
            >
              {isSubmitting && (
                <span className='size-4 animate-spin rounded-full border-t border-r border-white max-lg:size-2' />
              )}
              {t('modals.Confirm address')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default AddEditAddressManual;
